"use client";

import { Play, X } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { buildYouTubeEmbedUrl, buildYouTubeThumbnailUrl } from "@/lib/media/youtube";

// Vídeo do produto (YouTube) com FACHADA: até o clique só existe a
// miniatura (i.ytimg.com) e um botão; nenhum iframe e nenhum script do
// YouTube são carregados. Depois do clique o player do domínio
// youtube-nocookie.com entra no mesmo lugar, em 16:9, tocando na página.
//
// `autoStart`: a galeria monta este componente já como resposta a um clique
// do cliente (miniatura "Vídeo"), então pode ir direto para o player.

interface ProductVideoPlayerProps {
  youtubeId: string;
  productName: string;
  autoStart?: boolean;
  onClose?: () => void;
}

export function ProductVideoPlayer({ youtubeId, productName, autoStart = false, onClose }: ProductVideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(autoStart);
  const title = `Vídeo do produto ${productName}`;

  return (
    <div className="relative flex h-full w-full items-center justify-center bg-slate-950">
      <div className="relative aspect-video w-full overflow-hidden">
        {isPlaying ? (
          <iframe
            src={buildYouTubeEmbedUrl(youtubeId)}
            title={title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setIsPlaying(true)}
            aria-label={`Reproduzir vídeo de ${productName}`}
            className="group absolute inset-0 h-full w-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-secondary"
          >
            <Image
              src={buildYouTubeThumbnailUrl(youtubeId)}
              alt=""
              fill
              sizes="(min-width: 1024px) 50vw, 100vw"
              className="object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-slate-950/20 transition-colors group-hover:bg-slate-950/35">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-white shadow-lg transition-transform group-hover:scale-105 motion-reduce:transition-none">
                <Play className="ml-1 h-8 w-8 fill-current" aria-hidden="true" />
              </span>
            </span>
          </button>
        )}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar vídeo e voltar às fotos"
          title="Voltar às fotos"
          className="absolute left-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-foreground shadow-md transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
